/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Home, Volume2, VolumeX, Trophy, RotateCcw, Sparkles, Star, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import {
  SNAKES, LADDERS, QUESTION_SQUARES, PLAYER_CONFIGS,
  squareToPosition, positionToSquare, getSquareColor, getSquareCenter, getRandomQuestion,
  playDiceSound, playCorrectSound, playWrongSound as playWrongSfx, playClimbSound, playSlideSound,
  speakText, playStepSound,
} from "@/utils/snakeLadderData";
import { recordGameWon } from "@/utils/playerProfile";

// Floating particles component
const FloatingParticles = ({ count = 12, colors = ["#FFD700", "#FFA500", "#FF6B6B"] }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div key={i} className="absolute rounded-full"
        style={{ width: 3 + Math.random() * 4, height: 3 + Math.random() * 4, background: colors[i % colors.length], left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
        animate={{ y: [0, -30 - Math.random() * 40, 0], x: [0, (Math.random() - 0.5) * 30, 0], opacity: [0, 0.8, 0], scale: [0, 1.2, 0] }}
        transition={{ duration: 2 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 3, ease: "easeInOut" }}
      />
    ))}
  </div>
);

// Shape SVG for visual questions
const ShapeSVG = ({ shape, color, size = 48 }) => {
  const s = size;
  if (shape === "circle") return <svg width={s} height={s}><circle cx={s/2} cy={s/2} r={s*0.42} fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}80)` }}/></svg>;
  if (shape === "square") return <svg width={s} height={s}><rect x={s*0.12} y={s*0.12} width={s*0.76} height={s*0.76} rx={s*0.06} fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}80)` }}/></svg>;
  if (shape === "triangle") return <svg width={s} height={s}><polygon points={`${s/2},${s*0.08} ${s*0.08},${s*0.92} ${s*0.92},${s*0.92}`} fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}80)` }}/></svg>;
  if (shape === "star") return <svg width={s} height={s}><polygon points={`${s/2},${s*0.03} ${s*0.38},${s*0.36} ${s*0.03},${s*0.36} ${s*0.3},${s*0.6} ${s*0.18},${s*0.97} ${s/2},${s*0.74} ${s*0.82},${s*0.97} ${s*0.7},${s*0.6} ${s*0.97},${s*0.36} ${s*0.62},${s*0.36}`} fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}80)` }}/></svg>;
  if (shape === "heart") return <svg width={s} height={s} viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}80)` }}/></svg>;
  return null;
};

// Visual question prompt renderer
const VisualPrompt = ({ visual }) => {
  if (!visual) return null;
  if (visual.type === "letter") {
    return (
      <motion.div className="flex items-center justify-center"
        animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-6xl sm:text-7xl font-black font-fredoka" style={{ color: visual.color, textShadow: `0 0 20px ${visual.color}60, 0 4px 8px rgba(0,0,0,0.3)` }}>
          {visual.letter}
        </span>
      </motion.div>
    );
  }
  if (visual.type === "stars") {
    return (
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {Array.from({ length: visual.count }).map((_, i) => (
          <motion.span key={i} className="text-4xl sm:text-5xl"
            initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: i * 0.15, type: "spring" }}
            style={{ filter: "drop-shadow(0 0 8px rgba(234,179,8,0.6))" }}
          >⭐</motion.span>
        ))}
      </div>
    );
  }
  if (visual.type === "apples") {
    return (
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {Array.from({ length: visual.count }).map((_, i) => (
          <motion.span key={i} className="text-4xl sm:text-5xl"
            initial={{ scale: 0, y: 20 }} animate={{ scale: 1, y: 0 }}
            transition={{ delay: i * 0.15, type: "spring" }}
            style={{ filter: "drop-shadow(0 0 6px rgba(239,68,68,0.5))" }}
          >🍎</motion.span>
        ))}
      </div>
    );
  }
  if (visual.type === "compare") {
    return (
      <div className="flex items-center justify-center gap-4">
        <div className="flex flex-col items-center">
          <div className="flex gap-1 flex-wrap justify-center">
            {Array.from({ length: visual.left }).map((_, i) => (
              <motion.span key={i} className="text-3xl" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.1 }}>⭐</motion.span>
            ))}
          </div>
        </div>
        <span className="text-3xl font-black text-gold font-fredoka">VS</span>
        <div className="flex flex-col items-center">
          <div className="flex gap-1 flex-wrap justify-center">
            {Array.from({ length: visual.right }).map((_, i) => (
              <motion.span key={i} className="text-3xl" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + i * 0.1 }}>⭐</motion.span>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (visual.type === "colorTarget") {
    return (
      <motion.div className="flex flex-col items-center gap-2"
        animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-5xl sm:text-6xl" style={{ filter: `drop-shadow(0 0 10px ${visual.color}60)` }}>{visual.emoji}</span>
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white/30"
          style={{ backgroundColor: visual.color, boxShadow: `0 0 25px ${visual.color}80, inset 0 0 15px rgba(255,255,255,0.2)` }}
        />
      </motion.div>
    );
  }
  if (visual.type === "shapeTarget") {
    return (
      <motion.div className="flex items-center justify-center"
        animate={{ scale: [1, 1.08, 1], rotate: [0, 3, -3, 0] }} transition={{ duration: 3, repeat: Infinity }}
      >
        <ShapeSVG shape={visual.shape} color={visual.color} size={80} />
      </motion.div>
    );
  }
  if (visual.type === "emoji") {
    return (
      <motion.div className="flex items-center justify-center"
        animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}
      >
        <span className={visual.size === "big" ? "text-7xl sm:text-8xl" : "text-5xl sm:text-6xl"}
          style={{ filter: "drop-shadow(0 0 12px rgba(255,215,0,0.5))" }}
        >{visual.emoji}</span>
      </motion.div>
    );
  }
  return null;
};

// Visual option renderer
const VisualOption = ({ visual, label }) => {
  if (!visual) return <span>{label}</span>;
  if (visual.type === "letter") {
    return (
      <span className="text-3xl sm:text-4xl font-black font-fredoka" style={{ color: visual.color, textShadow: `0 0 10px ${visual.color}50` }}>
        {visual.letter}
      </span>
    );
  }
  if (visual.type === "dots") {
    const c = visual.count;
    if (c === 0) return <span className="text-lg font-fredoka text-gold-light/50">=</span>;
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="flex gap-0.5 flex-wrap justify-center">
          {Array.from({ length: Math.min(c, 6) }).map((_, i) => (
            <span key={i} className="text-lg sm:text-xl">🟡</span>
          ))}
        </div>
        <span className="text-lg font-black font-fredoka text-gold">{c}</span>
      </div>
    );
  }
  if (visual.type === "colorCircle") {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-3 border-white/30"
          style={{ backgroundColor: visual.color, boxShadow: `0 0 15px ${visual.color}60` }}
        />
      </div>
    );
  }
  if (visual.type === "shape") {
    return (
      <div className="flex items-center justify-center">
        <ShapeSVG shape={visual.shape} color={visual.color} size={48} />
      </div>
    );
  }
  if (visual.type === "emoji") {
    return (
      <div className="flex flex-col items-center gap-1">
        <span className="text-3xl sm:text-4xl">{visual.emoji}</span>
      </div>
    );
  }
  return <span>{label}</span>;
};

// 3D Dice Face
const DiceFace = ({ value, rolling }) => {
  const dotPositions = {
    1: [[50, 50]], 2: [[25, 25], [75, 75]], 3: [[25, 25], [50, 50], [75, 75]],
    4: [[25, 25], [75, 25], [25, 75], [75, 75]], 5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
    6: [[25, 25], [75, 25], [25, 50], [75, 50], [25, 75], [75, 75]],
  };
  const dots = dotPositions[value] || dotPositions[1];
  return (
    <motion.div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl"
      style={{ background: "linear-gradient(145deg, rgba(255,215,0,0.2), rgba(255,165,0,0.15))", border: "2px solid rgba(255,215,0,0.4)", boxShadow: rolling ? "0 0 30px rgba(255,215,0,0.6), inset 0 0 15px rgba(255,215,0,0.15)" : "0 0 15px rgba(255,215,0,0.3), inset 0 0 10px rgba(255,215,0,0.1)" }}
      animate={rolling ? { rotateX: [0, 360, 720], rotateY: [0, -360, -720], scale: [1, 0.8, 1.1, 0.9, 1] } : { rotateX: 0, rotateY: 0, scale: 1 }}
      transition={rolling ? { duration: 0.8, ease: "easeInOut" } : { type: "spring", stiffness: 200 }}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {dots.map(([cx, cy], i) => (
          <motion.circle key={i} cx={cx} cy={cy} r={10} fill="#FFD700" initial={false}
            animate={!rolling ? { scale: [0, 1.2, 1], opacity: [0, 1] } : {}}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            style={{ filter: "drop-shadow(0 0 4px rgba(255,215,0,0.8))" }}
          />
        ))}
      </svg>
    </motion.div>
  );
};

// Win floating emojis
const FloatingEmojis = () => {
  const emojis = ["🏆", "⭐", "🌟", "✨", "🎉", "🎊", "💫", "🥳", "👑", "🎯"];
  return (
    <div className="fixed inset-0 pointer-events-none z-40 overflow-hidden">
      {emojis.map((emoji, i) => (
        <motion.div key={i} className="absolute text-3xl sm:text-4xl" style={{ left: `${5 + (i * 9)}%` }}
          initial={{ y: "110vh", opacity: 0 }} animate={{ y: [null, "-10vh"], opacity: [0, 1, 1, 0], rotate: [0, (i % 2 === 0 ? 1 : -1) * 360] }}
          transition={{ duration: 4 + Math.random() * 2, delay: i * 0.3, ease: "easeOut" }}
        >{emoji}</motion.div>
      ))}
    </div>
  );
};

// SVG Overlay: Draw cartoon snakes and classic ladders
const BoardOverlay = () => {
  const snakeEntries = useMemo(() => Object.entries(SNAKES).map(([from, to]) => ({
    from: getSquareCenter(parseInt(from)),
    to: getSquareCenter(parseInt(to)),
    fromNum: parseInt(from),
    toNum: parseInt(to),
  })), []);

  const ladderEntries = useMemo(() => Object.entries(LADDERS).map(([from, to]) => ({
    from: getSquareCenter(parseInt(from)),
    to: getSquareCenter(parseInt(to)),
    fromNum: parseInt(from),
    toNum: parseInt(to),
  })), []);

  // Wavy snake path
  const snakePath = (from, to) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const waves = Math.max(2, Math.floor(dist / 12));
    const nx = -dy / dist;
    const ny = dx / dist;
    let d = `M ${from.x} ${from.y}`;
    for (let i = 1; i <= waves; i++) {
      const t = i / waves;
      const px = from.x + dx * t;
      const py = from.y + dy * t;
      const side = i % 2 === 0 ? 2.5 : -2.5;
      const cpx = from.x + dx * (t - 0.5 / waves) + nx * side;
      const cpy = from.y + dy * (t - 0.5 / waves) + ny * side;
      d += ` Q ${cpx} ${cpy} ${px} ${py}`;
    }
    return d;
  };

  // Ladder: two parallel lines + rungs
  const ladderParts = (from, to) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const nx = -dy / dist;
    const ny = dx / dist;
    const w = 1.2;
    const l1 = { x1: from.x + nx * w, y1: from.y + ny * w, x2: to.x + nx * w, y2: to.y + ny * w };
    const l2 = { x1: from.x - nx * w, y1: from.y - ny * w, x2: to.x - nx * w, y2: to.y - ny * w };
    const rungs = [];
    const rungCount = Math.max(3, Math.floor(dist / 8));
    for (let i = 1; i < rungCount; i++) {
      const t = i / rungCount;
      const px = from.x + dx * t;
      const py = from.y + dy * t;
      rungs.push({ x1: px + nx * w, y1: py + ny * w, x2: px - nx * w, y2: py - ny * w });
    }
    return { l1, l2, rungs };
  };

  const SNAKE_COLORS = [
    { body: "#4CAF50", accent: "#81C784" },  // green
    { body: "#FF9800", accent: "#FFB74D" },  // orange
    { body: "#F44336", accent: "#EF9A9A" },  // red  
    { body: "#2196F3", accent: "#90CAF9" },  // blue
    { body: "#E91E63", accent: "#F48FB1" },  // pink
    { body: "#9C27B0", accent: "#CE93D8" },  // purple
    { body: "#FF5722", accent: "#FF8A65" },  // deep orange
  ];

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none z-[5]" viewBox="0 0 100 100" preserveAspectRatio="none">
      {/* Snakes - cartoon style with faces */}
      {snakeEntries.map(({ from, to, fromNum }, idx) => {
        const sc = SNAKE_COLORS[idx % SNAKE_COLORS.length];
        return (
          <g key={`snake-${fromNum}`}>
            {/* Snake body - thick wavy line */}
            <path d={snakePath(from, to)} fill="none" stroke={sc.accent} strokeWidth="2.8" strokeLinecap="round" opacity="0.5" />
            <path d={snakePath(from, to)} fill="none" stroke={sc.body} strokeWidth="2" strokeLinecap="round" opacity="0.85" />
            {/* Snake head - circle with face */}
            <circle cx={from.x} cy={from.y} r="2.2" fill={sc.body} stroke={sc.accent} strokeWidth="0.5" />
            {/* Eyes */}
            <circle cx={from.x - 0.7} cy={from.y - 0.5} r="0.6" fill="white" />
            <circle cx={from.x + 0.7} cy={from.y - 0.5} r="0.6" fill="white" />
            <circle cx={from.x - 0.5} cy={from.y - 0.5} r="0.3" fill="#222" />
            <circle cx={from.x + 0.9} cy={from.y - 0.5} r="0.3" fill="#222" />
            {/* Smile */}
            <path d={`M ${from.x - 0.6} ${from.y + 0.4} Q ${from.x} ${from.y + 1.2} ${from.x + 0.6} ${from.y + 0.4}`} fill="none" stroke="#C62828" strokeWidth="0.3" />
            {/* Tongue */}
            <path d={`M ${from.x} ${from.y + 0.9} L ${from.x - 0.4} ${from.y + 1.6} M ${from.x} ${from.y + 0.9} L ${from.x + 0.4} ${from.y + 1.6}`} fill="none" stroke="#C62828" strokeWidth="0.2" />
            {/* Snake tail - small circle */}
            <circle cx={to.x} cy={to.y} r="0.8" fill={sc.body} opacity="0.7" />
          </g>
        );
      })}

      {/* Ladders - classic dark style with rungs */}
      {ladderEntries.map(({ from, to, fromNum }, idx) => {
        const { l1, l2, rungs } = ladderParts(from, to);
        return (
          <g key={`ladder-${fromNum}`}>
            {/* Side rails */}
            <line x1={l1.x1} y1={l1.y1} x2={l1.x2} y2={l1.y2} stroke="#5D4037" strokeWidth="1" strokeLinecap="round" />
            <line x1={l2.x1} y1={l2.y1} x2={l2.x2} y2={l2.y2} stroke="#5D4037" strokeWidth="1" strokeLinecap="round" />
            {/* Rungs */}
            {rungs.map((rung, ri) => (
              <line key={ri} x1={rung.x1} y1={rung.y1} x2={rung.x2} y2={rung.y2}
                stroke="#4E342E" strokeWidth="0.6" strokeLinecap="round" />
            ))}
          </g>
        );
      })}
    </svg>
  );
};

const SnakeLadderGame = ({ playerCount = 2, playerNames = [], onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [positions, setPositions] = useState(() => Array(playerCount).fill(0));
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [diceValue, setDiceValue] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [moving, setMoving] = useState(false);
  const [question, setQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [winner, setWinner] = useState(null);
  const [finishOrder, setFinishOrder] = useState([]); // Array of playerIdx in order they finish
  const [gameOver, setGameOver] = useState(false); // true when 3 players finished (or all-1 in 2/3P)
  const [soundOn, setSoundOn] = useState(true);
  const [gameLog, setGameLog] = useState([]);
  const [landingEffect, setLandingEffect] = useState(null);
  const [screenShake, setScreenShake] = useState(false);
  const [answeredCorrect, setAnsweredCorrect] = useState(null);
  const [winPhase, setWinPhase] = useState(0);
  const [steppingSquare, setSteppingSquare] = useState(null);
  const [trail, setTrail] = useState([]);

  const names = playerNames.length >= playerCount
    ? playerNames.slice(0, playerCount)
    : Array.from({ length: playerCount }, (_, i) => playerNames[i] || PLAYER_CONFIGS[i].name);

  useEffect(() => {
    if (!gameOver) { setWinPhase(0); return; }
    setWinPhase(1);
    const t2 = setTimeout(() => setWinPhase(2), 800);
    const t3 = setTimeout(() => setWinPhase(3), 2000);
    return () => { clearTimeout(t2); clearTimeout(t3); };
  }, [gameOver]);

  const rollDice = useCallback(() => {
    if (rolling || moving || question || gameOver) return;
    if (finishOrder.includes(currentPlayer)) return; // skip finished player
    setRolling(true);
    setFeedback(null);
    setLandingEffect(null);
    if (soundOn) {
      playDiceSound();
      speakText(`${names[currentPlayer]} rolls the dice!`, 1.0);
    }
    let rollCount = 0;
    const interval = setInterval(() => {
      setDiceValue(Math.floor(Math.random() * 6) + 1);
      rollCount++;
      if (rollCount >= 6) {
        clearInterval(interval);
        const finalValue = Math.floor(Math.random() * 6) + 1;
        setDiceValue(finalValue);
        setRolling(false);
        movePlayer(finalValue);
      }
    }, 60);
  }, [rolling, moving, question, gameOver, currentPlayer, finishOrder, soundOn]);

  const movePlayer = useCallback((steps) => {
    setMoving(true);
    setTrail([]);
    const current = positions[currentPlayer];
    let target = current + steps;
    if (target > 100) {
      setGameLog(prev => [...prev, `${PLAYER_CONFIGS[currentPlayer].charEmoji} ${names[currentPlayer]} butuh tepat ${100 - current} untuk menang`]);
      setMoving(false);
      nextTurn();
      return;
    }
    let step = 0;
    const doStep = () => {
      step++;
      const newPos = current + step;
      if (soundOn) playStepSound(step);
      setSteppingSquare(newPos);
      setTrail(prev => [...prev, newPos - 1].slice(-4));
      setPositions(prev => { const next = [...prev]; next[currentPlayer] = newPos; return next; });
      if (step >= steps) {
        setTimeout(() => {
          setSteppingSquare(null);
          setTrail([]);
          handleLanding(target);
        }, 200);
      } else {
        setTimeout(doStep, 250);
      }
    };
    setTimeout(doStep, 100);
  }, [currentPlayer, positions, soundOn]);

  const handlePlayerFinish = useCallback((playerIdx) => {
    setFinishOrder(prev => {
      if (prev.includes(playerIdx)) return prev;
      const newOrder = [...prev, playerIdx];
      const rank = newOrder.length;
      const rankLabel = rank === 1 ? "Juara 1!" : rank === 2 ? "Juara 2!" : "Juara 3!";
      setGameLog(p => [...p, `${PLAYER_CONFIGS[playerIdx].charEmoji} ${names[playerIdx]} ${rankLabel} 🏆`]);
      if (soundOn) speakText(`Congratulations! ${names[playerIdx]} ${rankLabel}`, 0.8);
      // Check if game should end: 3 finished OR only 1 player left
      const maxFinishers = Math.min(3, playerCount - 1);
      if (newOrder.length >= maxFinishers) {
        setGameOver(true);
        setWinner(newOrder[0]); // first place for confetti display
        recordGameWon("snakeladder");
      }
      return newOrder;
    });
    setMoving(false);
  }, [names, soundOn, playerCount]);

  const handleLanding = useCallback((square) => {
    const charE = PLAYER_CONFIGS[currentPlayer].charEmoji;
    if (square >= 100) {
      setPositions(prev => { const n = [...prev]; n[currentPlayer] = 100; return n; });
      handlePlayerFinish(currentPlayer);
      return;
    }
    if (SNAKES[square]) {
      setLandingEffect({ square, type: "snake" });
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 300);
      setGameLog(prev => [...prev, `${charE} ${names[currentPlayer]} kena ular! 🐍 Turun ${square} -> ${SNAKES[square]}`]);
      if (soundOn) { playSlideSound(); speakText(`Oh no! ${names[currentPlayer]} got the snake!`, 0.9); }
      setFeedback({ type: "snake", text: `Ular! Turun ke ${SNAKES[square]}`, from: square, to: SNAKES[square] });
      setTimeout(() => {
        setPositions(prev => { const n = [...prev]; n[currentPlayer] = SNAKES[square]; return n; });
        setFeedback(null); setLandingEffect(null); setMoving(false); nextTurn();
      }, 800);
      return;
    }
    if (LADDERS[square]) {
      setLandingEffect({ square, type: "ladder" });
      setGameLog(prev => [...prev, `${charE} ${names[currentPlayer]} naik tangga! 🪜 Naik ${square} -> ${LADDERS[square]}`]);
      if (soundOn) { playClimbSound(); speakText(`Yay! ${names[currentPlayer]} climbs the ladder!`, 0.9); }
      setFeedback({ type: "ladder", text: `Tangga! Naik ke ${LADDERS[square]}`, from: square, to: LADDERS[square] });
      const dest = LADDERS[square];
      setTimeout(() => {
        setPositions(prev => { const n = [...prev]; n[currentPlayer] = dest; return n; });
        if (dest >= 100) {
          setPositions(prev => { const n = [...prev]; n[currentPlayer] = 100; return n; });
          handlePlayerFinish(currentPlayer);
        } else { setFeedback(null); setLandingEffect(null); setMoving(false); nextTurn(); }
      }, 800);
      return;
    }
    if (QUESTION_SQUARES.includes(square)) {
      setLandingEffect({ square, type: "question" });
      setMoving(false);
      setTimeout(() => {
        const q = getRandomQuestion();
        setQuestion(q);
        setGameLog(prev => [...prev, `${charE} ${names[currentPlayer]} dapat pertanyaan! ❓`]);
        setLandingEffect(null);
        if (soundOn) speakText(q.q, 0.75);
      }, 400);
      return;
    }
    setMoving(false);
    nextTurn();
  }, [currentPlayer, soundOn, handlePlayerFinish]);

  const answerQuestion = useCallback((idx) => {
    if (!question) return;
    const correct = idx === question.answer;
    const charE = PLAYER_CONFIGS[currentPlayer].charEmoji;
    setAnsweredCorrect(correct);
    setTimeout(() => setAnsweredCorrect(null), 1500);
    if (correct) {
      if (soundOn) { playCorrectSound(); speakText("Correct! Pintar sekali!", 0.85); }
      const bonus = 2;
      const newPos = Math.min(positions[currentPlayer] + bonus, 100);
      setFeedback({ type: "correct", text: `Benar! Maju ${bonus} langkah bonus!` });
      setGameLog(prev => [...prev, `${charE} ${names[currentPlayer]} jawab benar! +${bonus}`]);
      setTimeout(() => {
        setPositions(prev => { const n = [...prev]; n[currentPlayer] = newPos; return n; });
        if (newPos >= 100) {
          handlePlayerFinish(currentPlayer);
        } else {
          nextTurn();
        }
        setQuestion(null); setFeedback(null);
      }, 1500);
    } else {
      if (soundOn) { playWrongSfx(); setTimeout(() => speakText(question.explain, 0.8), 500); }
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 400);
      setFeedback({ type: "wrong", text: `Salah! ${question.explain}` });
      setGameLog(prev => [...prev, `${charE} ${names[currentPlayer]} jawab salah. ${question.explain}`]);
      setTimeout(() => { setQuestion(null); setFeedback(null); nextTurn(); }, 2500);
    }
  }, [question, currentPlayer, positions, soundOn]);

  const nextTurn = useCallback(() => {
    if (gameOver) return;
    setCurrentPlayer(prev => {
      let next = (prev + 1) % playerCount;
      // Skip players who already finished — up to full cycle
      let skipped = 0;
      while (finishOrder.includes(next) && skipped < playerCount) {
        next = (next + 1) % playerCount;
        skipped++;
      }
      if (soundOn) setTimeout(() => speakText(`Turn ${names[next]}`, 0.9), 200);
      return next;
    });
  }, [playerCount, soundOn, names, gameOver, finishOrder]);

  const resetGame = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setPositions(Array(playerCount).fill(0)); setCurrentPlayer(0); setDiceValue(null);
    setQuestion(null); setFeedback(null); setWinner(null); setGameLog([]);
    setLandingEffect(null); setScreenShake(false); setAnsweredCorrect(null); setWinPhase(0);
    setSteppingSquare(null); setTrail([]);
    setFinishOrder([]); setGameOver(false);
  };

  const renderBoard = () => {
    const rows = [];
    for (let r = 0; r < 10; r++) {
      const cols = [];
      for (let c = 0; c < 10; c++) {
        const num = positionToSquare(r, c);
        const sqColor = getSquareColor(num);
        const isSnakeHead = SNAKES[num] !== undefined;
        const isLadderBottom = LADDERS[num] !== undefined;
        const isQuestion = QUESTION_SQUARES.includes(num);
        const playersHere = positions.map((pos, i) => pos === num ? i : -1).filter(i => i >= 0);
        const isLandingHere = landingEffect && landingEffect.square === num;
        const isFinish = num === 100;
        const isStepping = steppingSquare === num;
        const trailIndex = trail.indexOf(num);
        const isTrail = trailIndex >= 0;

        const isActive = isStepping || isLandingHere;

        cols.push(
          <div key={`${r}-${c}`} data-testid={`board-square-${num}`}
            className={`sl-board-cell relative aspect-square border-2 rounded-lg flex flex-col items-center justify-center overflow-hidden ${isFinish ? 'sl-finish-cell' : ''} ${isActive ? 'transition-all duration-200' : ''}`}
            style={{
              background: isLandingHere
                ? landingEffect.type === "snake" ? "rgba(239,68,68,0.5)" : landingEffect.type === "ladder" ? "rgba(34,197,94,0.5)" : "rgba(255,215,0,0.5)"
                : isStepping ? `${PLAYER_CONFIGS[currentPlayer].color}40`
                : isTrail ? `rgba(255,215,0,${0.12 + (trailIndex / trail.length) * 0.15})`
                : sqColor.bg,
              borderColor: isLandingHere
                ? landingEffect.type === "snake" ? "rgba(239,68,68,0.8)" : landingEffect.type === "ladder" ? "rgba(34,197,94,0.8)" : "rgba(255,215,0,0.8)"
                : isStepping ? PLAYER_CONFIGS[currentPlayer].color
                : sqColor.border,
              boxShadow: isLandingHere ? `0 0 10px ${landingEffect.type === "snake" ? "rgba(239,68,68,0.4)" : "rgba(34,197,94,0.4)"}`
                : isStepping ? `0 0 12px ${PLAYER_CONFIGS[currentPlayer].color}60`
                : "none",
              transform: isActive ? 'scale(1.05)' : 'none',
            }}
          >
            {/* Big number */}
            <span className={`font-fredoka font-black leading-none ${isFinish ? 'text-sm sm:text-base' : 'text-[11px] sm:text-sm'}`}
              style={{ color: isFinish ? '#B8860B' : num === 1 ? '#2E7D32' : 'rgba(0,0,0,0.65)', textShadow: isFinish ? '0 0 6px rgba(255,215,0,0.5)' : 'none' }}
            >{num}</span>

            {isQuestion && !isSnakeHead && !isLadderBottom && (
              <span className="text-xs sm:text-sm opacity-80">❓</span>
            )}
            {isFinish && (
              <span className="text-base sm:text-lg">🏆</span>
            )}

            {/* Player character pieces */}
            {playersHere.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-0 pb-0 z-10">
                {playersHere.map(pi => {
                  const isMovingPiece = pi === currentPlayer && moving;
                  const isFinished = finishOrder.includes(pi);
                  return (
                  <div key={pi}
                    className="relative flex items-center justify-center"
                    style={{
                      fontSize: playersHere.length > 2 ? "14px" : "18px",
                      transform: isMovingPiece ? 'translateY(-6px) scale(1.2)' : isFinished ? 'scale(0.8)' : 'none',
                      transition: 'transform 0.2s ease',
                      opacity: isFinished ? 0.5 : 1,
                    }}
                  >
                    <span style={{ filter: `drop-shadow(0 0 4px ${PLAYER_CONFIGS[pi].color})` }}>
                      {PLAYER_CONFIGS[pi].charEmoji}
                    </span>
                    {isFinished && <span className="absolute -top-1 -right-1 text-[8px]">🏆</span>}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }
      rows.push(<div key={r} className="grid grid-cols-10 gap-0.5">{cols}</div>);
    }
    return rows;
  };

  return (
    <motion.div className="flex flex-col items-center w-full max-w-5xl mx-auto px-2" data-testid="snake-ladder-game"
      animate={screenShake ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } : {}}
      transition={{ duration: 0.5 }}
    >
      {/* Confetti on game over */}
      {gameOver && (
        <>
          {winPhase >= 1 && <Confetti width={winW} height={winH} colors={["#FFD700","#FF6B6B","#4ECDC4","#A855F7","#FF69B4"]} recycle={false} numberOfPieces={200} gravity={0.15} />}
          {winPhase >= 3 && <Confetti width={winW} height={winH} colors={["#FFD700","#FFEC8B"]} recycle numberOfPieces={40} gravity={0.05} />}
        </>
      )}

      {/* Header */}
      <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 120 }} className="w-full mb-3">
        <div className="glass-card-strong rounded-2xl p-3 relative overflow-hidden">
          <FloatingParticles count={3} colors={["#FFD70030"]} />
          <div className="flex items-center justify-between mb-2 relative z-10">
            <div className="flex items-center gap-2">
              <motion.span className="text-xl" animate={{ rotate: [0, 360] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }}>🎲</motion.span>
              <h2 className="font-fredoka text-lg sm:text-xl font-bold text-gold sl-title-glow">Ular Tangga Pintar</h2>
            </div>
            <div className="flex gap-1.5">
              <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="sl-sound-toggle">
                {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </Button>
              <Button onClick={resetGame} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-8 px-2" data-testid="sl-reset-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" />Ulang
              </Button>
              <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-8 px-2" data-testid="sl-menu-btn">
                <Home className="w-3.5 h-3.5 mr-1" />Menu
              </Button>
            </div>
          </div>

          {/* Players bar with characters */}
          <div className="grid gap-1.5 relative z-10" style={{ gridTemplateColumns: `repeat(${playerCount}, 1fr)` }}>
            {names.map((name, i) => {
              const isActive = currentPlayer === i && !gameOver;
              const finishRank = finishOrder.indexOf(i);
              const isFinished = finishRank >= 0;
              return (
                <div key={i} className="text-center p-1.5 rounded-xl border-2 relative overflow-hidden"
                  style={{
                    borderColor: isActive ? PLAYER_CONFIGS[i].color : isFinished ? PLAYER_CONFIGS[i].color + '40' : 'transparent',
                    background: isActive ? `${PLAYER_CONFIGS[i].color}15` : isFinished ? `${PLAYER_CONFIGS[i].color}08` : 'transparent',
                    opacity: isFinished ? 0.7 : 1,
                  }}
                >
                  <span className="text-base block">{PLAYER_CONFIGS[i].charEmoji}</span>
                  <p className="font-fredoka text-[10px] font-bold truncate" style={{ color: PLAYER_CONFIGS[i].color }}>{name}</p>
                  <p className="font-fredoka text-[9px] text-gold-light/40">
                    {isFinished ? ['🥇','🥈','🥉'][finishRank] || '✅' : `Kotak ${positions[i]}`}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Board + Controls */}
      <div className="flex flex-col lg:flex-row gap-3 w-full items-start">
        {/* Board with SVG overlay */}
        <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 100 }}
          className="rounded-2xl p-2 sm:p-3 flex-1 w-full lg:max-w-[700px] relative overflow-hidden border-4"
          style={{ background: 'rgba(30,20,10,0.85)', borderColor: '#2E7D6F' }}
        >
          <div className="relative z-10">
            {/* The board grid */}
            <div className="space-y-0.5 relative">
              {renderBoard()}
              {/* SVG Overlay for snakes and ladders */}
              <BoardOverlay />
            </div>
          </div>
          {/* Legend */}
          <div className="flex justify-center gap-4 mt-2.5 relative z-10">
            {[
              { icon: "🐍", label: "Ular", color: "#EF4444" },
              { icon: "🪜", label: "Tangga", color: "#22C55E" },
              { icon: "❓", label: "Pertanyaan", color: "#FFD700" },
              { icon: "👑", label: "Finish", color: "#FFA500" },
            ].map(item => (
              <motion.span key={item.label} className="text-[10px] font-fredoka font-bold flex items-center gap-0.5" style={{ color: `${item.color}80` }}
                whileHover={{ scale: 1.1, color: item.color }}
              >{item.icon} {item.label}</motion.span>
            ))}
          </div>
        </motion.div>

        {/* Right Panel */}
        <div className="w-full lg:w-72 space-y-3">
          {/* Dice Area */}
          <motion.div className="glass-card-strong rounded-2xl p-4 text-center relative overflow-hidden"
            initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2, type: "spring" }}
          >
            {!gameOver ? (
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-2xl" style={{ filter: `drop-shadow(0 0 4px ${PLAYER_CONFIGS[currentPlayer].color})` }}
                  >{PLAYER_CONFIGS[currentPlayer].charEmoji}</span>
                  <div>
                    <span className="font-fredoka text-xs text-gold-light/60 block">Giliran:</span>
                    <span className="font-fredoka font-bold text-sm" style={{ color: PLAYER_CONFIGS[currentPlayer].color }}
                    >{names[currentPlayer]}</span>
                    {finishOrder.length > 0 && (
                      <span className="font-fredoka text-[10px] text-gold-light/40 block">
                        ({finishOrder.length} pemain sudah selesai)
                      </span>
                    )}
                  </div>
                </div>

                <motion.button data-testid="roll-dice-btn" onClick={rollDice} disabled={rolling || moving || !!question}
                  whileHover={!(rolling || moving || question) ? { scale: 1.05 } : {}}
                  whileTap={!(rolling || moving || question) ? { scale: 0.95 } : {}}
                  className={`mx-auto flex items-center justify-center cursor-pointer transition-all ${(rolling || moving || question) ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <DiceFace value={diceValue || 1} rolling={rolling} />
                </motion.button>

                <p className="font-fredoka text-xs mt-2 h-5">
                  {rolling ? <span className="text-gold">Mengocok...</span>
                    : moving ? <span className="text-gold-light/60">Berjalan...</span>
                    : question ? <span className="text-gold animate-pulse">Jawab!</span>
                    : <span className="text-gold-light/40">Klik dadu!</span>}
                </p>

                {diceValue && !rolling && (
                  <div className="mt-1">
                    <span className="font-fredoka text-2xl font-black" style={{ color: PLAYER_CONFIGS[currentPlayer].color }}>+{diceValue}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center relative z-10">
                <p className="font-fredoka text-lg font-black text-gold mb-3">Hasil Akhir!</p>
                <div className="space-y-2 mb-3">
                  {finishOrder.map((pi, rank) => (
                    <div key={pi} className="flex items-center gap-2 p-2 rounded-xl border"
                      style={{ borderColor: PLAYER_CONFIGS[pi].color + '60', background: PLAYER_CONFIGS[pi].color + '15' }}
                    >
                      <span className="font-fredoka font-black text-lg" style={{ color: rank === 0 ? '#FFD700' : rank === 1 ? '#C0C0C0' : '#CD7F32' }}>
                        {rank === 0 ? '🥇' : rank === 1 ? '🥈' : '🥉'}
                      </span>
                      <span className="text-xl">{PLAYER_CONFIGS[pi].charEmoji}</span>
                      <span className="font-fredoka font-bold text-sm" style={{ color: PLAYER_CONFIGS[pi].color }}>{names[pi]}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button onClick={resetGame} className="flex-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-fredoka font-bold rounded-xl" data-testid="sl-play-again">
                    <RotateCcw className="w-4 h-4 mr-1" />Main Lagi
                  </Button>
                  <Button onClick={onBackToMenu} variant="outline" className="glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
                    <Home className="w-4 h-4 mr-1" />Menu
                  </Button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Feedback */}
          <AnimatePresence>
            {feedback && (
              <motion.div initial={{ y: -20, opacity: 0, scale: 0.8 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className={`glass-card-strong rounded-2xl p-4 text-center border-2 relative overflow-hidden ${
                  feedback.type === "correct" ? "sl-feedback-correct" : feedback.type === "wrong" ? "sl-feedback-wrong" :
                  feedback.type === "snake" ? "sl-feedback-snake" : feedback.type === "ladder" ? "sl-feedback-ladder" : ""
                }`} data-testid="sl-feedback"
              >
                <motion.span className="text-3xl mb-2 block"
                  animate={feedback.type === "correct" ? { rotate: [0, -20, 20, -10, 10, 0], scale: [1, 1.3, 1] }
                    : feedback.type === "snake" ? { y: [0, 5, 0], rotate: [0, -5, 5, 0] }
                    : feedback.type === "ladder" ? { y: [0, -8, 0], scale: [1, 1.2, 1] }
                    : { scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.6, repeat: feedback.type === "correct" ? 2 : 1 }}
                >
                  {feedback.type === "correct" && "🎉"}{feedback.type === "wrong" && "💡"}{feedback.type === "snake" && "🐍"}{feedback.type === "ladder" && "🪜"}
                </motion.span>
                <p className={`font-fredoka text-sm font-bold relative z-10 ${
                  feedback.type === "correct" ? "text-green-300" : feedback.type === "wrong" ? "text-amber-300" : feedback.type === "snake" ? "text-red-300" : "text-green-300"
                }`}>{feedback.text}</p>
                {(feedback.from && feedback.to) && (
                  <motion.p className="font-fredoka text-xs text-gold-light/40 mt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                    {feedback.from} {feedback.type === "snake" ? "⬇" : "⬆"} {feedback.to}
                  </motion.p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Game Log */}
          <motion.div className="glass-card-strong rounded-2xl p-3 max-h-44 overflow-y-auto custom-scrollbar"
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-gold/60" />
              <p className="font-fredoka text-xs font-bold text-gold-light/60">Log Permainan:</p>
            </div>
            {gameLog.length === 0 ? (
              <p className="font-fredoka text-xs text-gold-light/30">Lempar dadu untuk mulai!</p>
            ) : (
              <div className="space-y-1">
                {[...gameLog].reverse().slice(0, 12).map((log, i) => (
                  <motion.p key={gameLog.length - i} className="font-fredoka text-[10px] text-gold-light/45 leading-relaxed"
                    initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.03 }}
                  ><span className="text-gold-light/25 mr-1">{gameLog.length - i}.</span>{log}</motion.p>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Question Modal - LIGHTWEIGHT */}
      <AnimatePresence>
        {question && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="sl-question-modal"
          >
            <motion.div initial={{ scale: 0.8, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: -30 }}
              transition={{ duration: 0.25 }}
              className="glass-card-strong rounded-3xl p-5 sm:p-6 border-2 border-gold/40 max-w-md w-full relative overflow-hidden"
            >
              {/* Player + Category */}
              <div className="text-center mb-2">
                <span className="text-2xl" style={{ filter: `drop-shadow(0 0 4px ${PLAYER_CONFIGS[currentPlayer].color})` }}>
                  {PLAYER_CONFIGS[currentPlayer].charEmoji}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-fredoka font-bold ml-2"
                  style={{
                    background: question.category === "huruf" ? "rgba(168,85,247,0.15)" : question.category === "angka" ? "rgba(59,130,246,0.15)" : question.category === "warna" ? "rgba(236,72,153,0.15)" : "rgba(34,197,94,0.15)",
                    color: question.category === "huruf" ? "#C084FC" : question.category === "angka" ? "#60A5FA" : question.category === "warna" ? "#F472B6" : "#4ADE80",
                  }}
                >
                  {question.category === "huruf" && "Huruf"}
                  {question.category === "angka" && "Angka"}
                  {question.category === "warna" && "Warna"}
                  {question.category === "bentuk" && "Bentuk"}
                </span>
              </div>

              {/* Visual Prompt */}
              <div className="flex items-center justify-center mb-3 min-h-[70px]">
                <VisualPrompt visual={question.visual} />
              </div>

              {/* Question text */}
              <p className="font-fredoka text-sm sm:text-base font-bold text-gold/80 text-center mb-3">{question.q}</p>

              {/* Answer options */}
              <div className="grid grid-cols-2 gap-2">
                {question.options.map((opt, i) => (
                  <button key={i} data-testid={`sl-answer-${i}`}
                    onClick={() => answerQuestion(i)}
                    className="glass-card border-2 rounded-2xl p-3 transition-all hover:scale-105 active:scale-95 min-h-[60px] flex items-center justify-center"
                    style={{ borderColor: 'rgba(255,215,0,0.2)' }}
                  >
                    <div className="flex flex-col items-center justify-center gap-1">
                      <VisualOption visual={opt.visual} label={opt.label} />
                    </div>
                  </button>
                ))}
              </div>

              <p className="font-fredoka text-[10px] text-gold-light/30 text-center mt-2">
                Jawab benar = bonus 2 langkah!
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Answer feedback overlays - simple */}
      <AnimatePresence>
        {answeredCorrect === true && (
          <motion.div className="fixed inset-0 pointer-events-none z-[55] flex items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <span className="text-7xl">🌟</span>
          </motion.div>
        )}
        {answeredCorrect === false && (
          <motion.div className="fixed inset-0 pointer-events-none z-[55] flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <span className="text-5xl">😅</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SnakeLadderGame;
